import { z } from 'zod';
import { roleSchema } from './roles';

/**
 * Membership approval state (M15). Only self-registration produces `pending`; an
 * admin-created account is `approved` on insert, because whoever created it did the vetting.
 * `rejected` is terminal for login — the account keeps existing so the decision, and the
 * email it was made against, stay on the record.
 */
export const approvalStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

/** Safe user projection returned by the API — never includes the password hash. */
export const userSummarySchema = z.object({
  id: z.uuid(),
  email: z.string(),
  nameFirst: z.string().nullable(),
  nameLast: z.string().nullable(),
  mobile: z.string().nullable(),
  roles: z.array(roleSchema),
  relatedCompany: z.array(z.uuid()),
  createdAt: z.number().int(),
  approvalStatus: approvalStatusSchema,
  approvalDecidedAt: z.number().int().nullable(),
  approvalRejectReason: z.string().nullable(),
});
export type UserSummary = z.infer<typeof userSummarySchema>;

/**
 * `POST /admin/users/:id/reject` — the reason is optional and operator-facing only. It is
 * stored for the audit trail and shown in the panel; the applicant is not emailed a rejection
 * (the gate notifies on approval only).
 */
export const userRejectSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type UserRejectInput = z.infer<typeof userRejectSchema>;

export const userCreateSchema = z.object({
  email: z.email(),
  password: z.string().min(8),
  nameFirst: z.string().optional(),
  nameLast: z.string().optional(),
  mobile: z.string().optional(),
  roles: z.array(roleSchema).default([]),
  relatedCompany: z.array(z.uuid()).default([]),
});
export type UserCreateInput = z.infer<typeof userCreateSchema>;

/** All fields optional; only provided fields are updated. */
export const userUpdateSchema = z.object({
  email: z.email().optional(),
  password: z.string().min(8).optional(),
  nameFirst: z.string().optional(),
  nameLast: z.string().optional(),
  mobile: z.string().optional(),
  roles: z.array(roleSchema).optional(),
  relatedCompany: z.array(z.uuid()).optional(),
});
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;
